import classNames from 'classnames';
import pluralize from 'pluralize';
import { memo, useMemo } from 'react';

import { Comment as CommentIcon, Stack as StackIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';

export type AssetMetaDataProps = {
  assetVersionCount: number;
  onCommentsClick?: () => void;
  onVersionsClick?: () => void;
  openCommentCount: number;
  shouldShowDiscussions: boolean;
  shouldShowVersions: boolean;
  pagesCount: number;
};

export const AssetMetaData = memo(
  ({
    assetVersionCount,
    onCommentsClick,
    onVersionsClick,
    openCommentCount,
    shouldShowDiscussions,
    shouldShowVersions,
    pagesCount,
  }: AssetMetaDataProps) => {
    const showVersions = shouldShowVersions && assetVersionCount > 1;
    const showDiscussions = shouldShowDiscussions && openCommentCount > 0;

    const pagesCountText = useMemo(() => {
      if (!pagesCount) {
        return '';
      }
      const textToShow = pluralize('page', pagesCount, true);
      return showVersions || showDiscussions ? `· ${textToShow}` : textToShow;
    }, [pagesCount, showDiscussions, showVersions]);

    return (
      <div
        className={classNames(
          'pointer-events-none flex items-center gap-1',
          showVersions || showDiscussions ? '-ml-1' : '',
        )}
      >
        {showVersions && (
          <Button
            appearance="ghost"
            className={classNames('h-5 px-1', onVersionsClick ? 'pointer-events-auto' : 'pointer-events-none')}
            color="grey"
            data-disableselect="true"
            data-testid="TABLE_ROW_VERSIONS_INDICATOR"
            onClick={(event) => {
              event.preventDefault();
              onVersionsClick?.();
            }}
            /**
             * Prevents the button from triggering selection of the row when clicking on the button.
             */
            onMouseDown={(event) => event.stopPropagation()}
            prefix={<StackIcon className="size-3" />}
            size="small"
          >
            {assetVersionCount}
          </Button>
        )}

        {showDiscussions && (
          <Button
            appearance="ghost"
            className={classNames('h-5 px-1', onCommentsClick ? 'pointer-events-auto' : 'pointer-events-none')}
            color="grey"
            data-disableselect="true"
            data-testid="TABLE_ROW_DISCUSSIONS_INDICATOR"
            onClick={(event) => {
              event.preventDefault();
              onCommentsClick?.();
            }}
            /**
             * Prevents the button from triggering selection of the row when clicking on the button.
             */
            onMouseDown={(event) => event.stopPropagation()}
            prefix={<CommentIcon className="size-3" />}
            size="small"
          >
            {openCommentCount}
          </Button>
        )}
        {!!pagesCountText && <p className="text-12">{pagesCountText}</p>}
      </div>
    );
  },
);

AssetMetaData.displayName = 'AssetMetaData';
